import { emailSignatureSideEditorServicesContext } from '@context/email-signature-services-context';
import { generateNewImageId } from '@modules/email-signatures-helpers';
import { downloadRemoteImage } from '@modules/image-helpers';
// hooks and context
import type { EmailSignaturesActions } from '@reducers/email-signature-reducer';
import { type FC, useCallback } from 'react';

import type { EmailSignatureFieldTypes } from '../../consts';
// types
import {
  type EmailSignatureImagesTypes,
  EmailSignatureShapes,
  EmailSignatureSizes,
  type EmailSignatureV2,
} from '../../types';
// utils
import type EmailSignatureImagesUploader from '../EmailSignatureImagesUploader';
import {
  useBannerServices,
  useContactServices,
  useEnvironmentalFooterServices,
  useImageServices,
  useMetadataServices,
  useSocialServices,
} from './services';

interface SideEditorCoreServicesProviderProps {
  emailSignature: EmailSignatureV2;
  emailSignatureDispatch: (action: EmailSignaturesActions) => void;
  emailSignatureHasBeenLoaded: boolean;
  handleOnErrorUploading: (fieldType: EmailSignatureFieldTypes) => void;
  isInTransitionState?: boolean;
  isEditingSavedEmailSignature: boolean;
  isEverythingEmpty: boolean;
  uploadEmailSignatureFieldImagesThunk: (
    uploadImage: (emailSignatureImagesUploader: EmailSignatureImagesUploader) => void,
  ) => void;
}

const CARD_IMAGE_TIMEOUT_MS = 6000;

/*
    This component is a context wrapper that provides all the core services for the email signature side editor,
    it should be used with the email signature Side Editor component. This component allows SideEditor component to
    be reused in different editors (currently used in EmailSignatureMainEditor and EmailSignatureOnboardingEditor)
*/

const SideEditorCoreServicesProvider: FC<React.PropsWithChildren<SideEditorCoreServicesProviderProps>> = ({
  emailSignature,
  emailSignatureDispatch,
  emailSignatureHasBeenLoaded,
  isInTransitionState = false,
  isEverythingEmpty,
  isEditingSavedEmailSignature,
  handleOnErrorUploading,
  uploadEmailSignatureFieldImagesThunk,

  children,
}) => {
  const { isSignatureLocked } = useMetadataServices({ emailSignature });

  const {
    originalImagesUrls,
    handleUpdateImageShape,
    handleUpdateLocalCroppedImage,
    localCroppedImagesUrls,
    handleUploadOriginalImageUrl,
    displayImagesInViewMode,
    handleChangeImageViewMode,
  } = useImageServices({
    emailSignature,
    emailSignatureDispatch,
    emailSignatureHasBeenLoaded,
    isEditingSavedEmailSignature,
    handleOnErrorUploading,
    uploadEmailSignatureFieldImagesThunk,
  });

  const {
    handleChangeBannerViewMode,
    displayBannerInViewMode,
    localCroppedBannerImageUrl,
    handleCroppedBannerImageChange,
    originalBannerImageUrl,
    handleUploadOriginalBannerImageUrl,
  } = useBannerServices({
    emailSignature,
    emailSignatureDispatch,
    emailSignatureHasBeenLoaded,
    isEditingSavedEmailSignature,
    handleOnErrorUploading,
    uploadEmailSignatureFieldImagesThunk,
  });

  const { onEditFieldWidth, hasEditedFieldWidth } = useContactServices({
    emailSignature,
    emailSignatureDispatch,
    emailSignatureHasBeenLoaded,
    handleOnErrorUploading,
    uploadEmailSignatureFieldImagesThunk,
  });

  useSocialServices({
    emailSignature,
    emailSignatureDispatch,
    emailSignatureHasBeenLoaded,
    handleOnErrorUploading,
    uploadEmailSignatureFieldImagesThunk,
  });

  useEnvironmentalFooterServices({
    emailSignature,
    emailSignatureDispatch,
    emailSignatureHasBeenLoaded,
    handleOnErrorUploading,
    uploadEmailSignatureFieldImagesThunk,
  });

  const addCardImageToSignature = useCallback(
    async (imageUrl: string, isPrimary: boolean, type: EmailSignatureImagesTypes) => {
      const blob = await downloadRemoteImage(imageUrl, CARD_IMAGE_TIMEOUT_MS);
      if (!blob) return;

      const url = URL.createObjectURL(blob);
      const newImageId = generateNewImageId();

      handleUploadOriginalImageUrl(url, newImageId);
      const imageInitialShape =
        type === 'profile-picture' ? EmailSignatureShapes.CIRCLE : EmailSignatureShapes.SQUARE;

      emailSignatureDispatch({
        type: 'ADD_IMAGE',
        value: {
          id: newImageId,
          externalLink: '',
          imageUrl: url,
          shape: imageInitialShape,
          type: type,
          size: EmailSignatureSizes.MEDIUM,
          isPrimary: isPrimary,
          originalImageUrl: '',
          crop: null,
        },
      });
      handleUpdateLocalCroppedImage(url, newImageId, imageInitialShape, null);
    },
    [emailSignatureDispatch, handleUpdateLocalCroppedImage, handleUploadOriginalImageUrl],
  );

  return (
    <emailSignatureSideEditorServicesContext.Provider
      value={{
        isInTransitionState,
        emailSignature,
        emailSignatureDispatch,
        emailSignatureHasBeenLoaded,
        isEditingSavedEmailSignature,
        isSignatureLocked,
        handleChangeBannerViewMode,
        displayBannerInViewMode,
        onEditFieldWidth,
        hasEditedFieldWidth,
        isEverythingEmpty,
        localCroppedBannerImageUrl,
        handleCroppedBannerImageChange,
        originalBannerImageUrl,
        handleUploadOriginalBannerImageUrl,
        originalImagesUrls,
        handleUpdateImageShape,
        handleUpdateLocalCroppedImage,
        localCroppedImagesUrls,
        handleUploadOriginalImageUrl,
        displayImagesInViewMode,
        handleChangeImageViewMode,
        addCardImageToSignature,
      }}
    >
      {children}
    </emailSignatureSideEditorServicesContext.Provider>
  );
};

export default SideEditorCoreServicesProvider;
